import sessionId from '@/Utils/SessionId';
import { getUnifiedSessionId } from '@/Providers/BrowserSessionProvider';
import {
  adBlockCompleted,
  adBlockStarted,
  adCompleted,
  adExited,
  adImpression,
  adMuted,
  adPaused,
  adResumed,
  adUnmuted,
} from './Segment';

export enum AdEvents {
  AdBlockStarted = 'Ad Block Started',
  AdBlockCompleted = 'Ad Block Completed',
  AdImpression = 'Ad Impression',
  AdCompleted = 'Ad Completed',
  AdMuted = 'Ad Muted',
  AdUnmuted = 'Ad Unmuted',
  AdPaused = 'Ad Paused',
  AdResumed = 'Ad Resumed',
  AdExited = 'Ad Exited',
}

const PLATFORM = 'Web';
const NETWORK = 'WiFi';

export enum Reasons {
  UserExit = 'user_exit',
  Completed = 'completed',
  Error = 'error',
}

export const COMPLETION_PERCENTAGE_THRESHOLD = 95;

const INTERACTION_TYPE = {
  [AdEvents.AdMuted]: 'mute',
  [AdEvents.AdUnmuted]: 'unmute',
  [AdEvents.AdPaused]: 'pause',
  [AdEvents.AdResumed]: 'resume',
  [AdEvents.AdExited]: 'exit',
};

const EVENT_TO_FUNC_MAP = {
  [AdEvents.AdBlockStarted]: adBlockStarted,
  [AdEvents.AdBlockCompleted]: adBlockCompleted,
  [AdEvents.AdUnmuted]: adUnmuted,
  [AdEvents.AdMuted]: adMuted,
  [AdEvents.AdPaused]: adPaused,
  [AdEvents.AdResumed]: adResumed,
  [AdEvents.AdExited]: adExited,
};

export interface ContentPayloadProps {
  id: string;
  type: string;
  duration: number;
}

export interface AdPodPayloadProps {
  ad_block_id?: string;
  ad_position?: string;
  total_ads_in_block?: number;
  total_block_duration?: number;
  action_adblock_timestamp?: number;
}

export interface AdPayloadProps {
  adId?: string;
  adSystem?: string;
  index?: number;
  duration?: number;
  action_timestamp?: number;
  watched_duration?: number;
  completion_rate?: number;
  reason?: Reasons;
}
export interface VideoContentPayloadProps {
  content_position?: number;
  stop_reason?: Reasons;
}

interface AdBlockProps {
  ad_block_id: string; // Grouped breakId - e.g. "PreRoll_0", "MidRoll_1119"
  ad_position: string; // 'pre-roll'; future: 'mid-roll', 'post-roll'
  total_ads_in_block: number; // count of ads in the grouped block
  total_block_duration: number; // sum of all ad durations in block
  content_id: string;
  content_type: string;
  platform: string;
  network: string;
  session_id: string; // playback session
}

// AdImpression
interface AdImpressionProps {
  ad_id: string; // VAST Ad ID - e.g. "7060817434"
  ad_campaign_id?: string; // Google Campaign ID from tracking URLs
  ad_block_id: string; // Grouped breakId - e.g. "PreRoll_0", "MidRoll_1119", "MidRoll_2787"
  ad_position: string; // 'pre-roll'  future: 'mid-roll', 'post-roll'
  ad_duration: number; // seconds - parsed from VAST Duration e.g. 15 seconds
  ad_index: number; // 1st, 2nd, 3rd ad in block (1-based)
  total_ads_in_block: number; // total ads in the grouped block
  content_id: string; // VOD asset being played
  content_type?: string; // 'Movie' | 'Episode' | 'Sport';
  platform: string;
  network: string;
  ad_server: string; // 'SSAI' | 'GAM'; // "GAM" from VAST AdSystem
  fallback_content?: boolean; // true if Sky promo
}

// AdCompleted
interface AdCompletedProps {
  ad_id: string; // VAST Ad ID - e.g. "7060817434"
  ad_campaign_id?: string;
  ad_block_id: string; // Grouped breakId - e.g. "PreRoll_0"
  ad_position: string; // 'pre-roll';
  ad_duration: number; // seconds from VAST Duration
  watched_duration: number; // actual time watched
  completion_rate: number; // percentage watched (watched_duration/ad_duration * 100)
  ad_index: number; // position in block (1-based)
  total_ads_in_block: number;
  content_id: string;
  platform: string;
  network: string;
  reason: string; // 'completed' | 'user_exit' | 'error';
}
// AdMuted AdUnmuted AdPaused AdResumed AdExited
interface AdInteractionProps {
  ad_id: string; // VAST Ad ID - e.g. "7060817434"
  ad_block_id: string; // Grouped breakId - e.g. "PreRoll_0"
  action_timestamp: number; // seconds into current ad
  action_adblock_timestamp: number; // seconds into ad block
  ad_duration: number; // total ad duration from VAST
  content_id: string;
  platform: string;
  interaction_type: string;
}

export const trackSsaiAdEvent = (
  eventType: AdEvents,
  contentPayload: ContentPayloadProps,
  adPodPayload: AdPodPayloadProps,
  adPayload: AdPayloadProps,
) => {
  switch (eventType) {
    case AdEvents.AdBlockStarted:
    case AdEvents.AdBlockCompleted:
      EVENT_TO_FUNC_MAP[eventType]({
        ad_block_id: adPodPayload.ad_block_id,
        ad_position: adPodPayload.ad_position,
        total_ads_in_block: adPodPayload.total_ads_in_block,
        total_block_duration: adPodPayload.total_block_duration,
        content_id: contentPayload.id,
        content_type: contentPayload.type,
        platform: PLATFORM,
        network: NETWORK,
        session_id: getUnifiedSessionId() ?? sessionId,
      });
      break;
    case AdEvents.AdImpression:
      adImpression({
        ad_id: adPayload.adId,
        ad_block_id: adPodPayload.ad_block_id,
        ad_position: adPodPayload.ad_position,
        ad_duration: adPayload.duration,
        ad_index: adPayload.index,
        total_ads_in_block: adPodPayload.total_ads_in_block,
        content_id: contentPayload.id,
        content_type: contentPayload.type,
        platform: PLATFORM,
        network: NETWORK,
        ad_server: adPayload.adSystem,
      });
      break;
    case AdEvents.AdCompleted:
      adCompleted({
        ad_id: adPayload.adId,
        ad_block_id: adPodPayload.ad_block_id,
        ad_position: adPodPayload.ad_position,
        ad_duration: adPayload.duration,
        watched_duration: adPayload.watched_duration,
        completion_rate: adPayload.completion_rate,
        ad_index: adPayload.index,
        total_ads_in_block: adPodPayload.total_ads_in_block,
        content_id: contentPayload.id,
        platform: PLATFORM,
        network: NETWORK,
        reason: adPayload.reason,
      });
      break;
    case AdEvents.AdMuted:
    case AdEvents.AdUnmuted:
    case AdEvents.AdPaused:
    case AdEvents.AdResumed:
    case AdEvents.AdExited:
      EVENT_TO_FUNC_MAP[eventType]({
        ad_id: adPayload.adId,
        ad_block_id: adPodPayload.ad_block_id,
        action_timestamp: adPayload.action_timestamp,
        action_adblock_timestamp: adPodPayload.action_adblock_timestamp,
        ad_duration: adPayload.duration,
        content_id: contentPayload.id,
        platform: PLATFORM,
        interaction_type: INTERACTION_TYPE[eventType],
      });
      break;
    default:
      break;
  }
};
